import React, { useContext, useEffect, useState } from "react";
import { CurrentUserContext } from "App";
import defaultAvatar from "assets/sampleAvatar.png";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

export default function FeedbackPage() {
  const { currentUser } = useContext(CurrentUserContext);
  const isAdmin = currentUser?.username === "kunsh";

  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  // Admin: fetch all feedback
  useEffect(() => {
    if (!isAdmin) return;
    setLoadingFeedback(true);
    fetch(`${API_URL}/getFeedback`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setFeedbackList(r.feedback || []);
        }
      })
      .catch((err) => console.error("Error fetching feedback:", err))
      .finally(() => setLoadingFeedback(false));
  }, [isAdmin, currentUser.token]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitting(true);
    setError("");

    fetch(`${API_URL}/submitFeedback`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${currentUser.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ message: message.trim() }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setSubmitted(true);
          setMessage("");
          // If admin, refresh the list
          if (isAdmin) {
            setFeedbackList((prev) => [
              {
                feedback_id: Date.now(),
                message: message.trim(),
                created_at: new Date().toISOString(),
                username: currentUser.username,
                avatar: currentUser.avatar,
              },
              ...prev,
            ]);
          }
        } else {
          setError(r.message || "Failed to submit feedback.");
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSubmitting(false));
  }

  function formatTime(dateStr) {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }

  return (
    <div
      className="d-flex flex-column align-items-center"
      style={{
        minHeight: "100vh",
        padding: "2rem 1rem",
        maxWidth: 700,
        margin: "0 auto",
      }}
    >
      <h2
        style={{
          color: "var(--main-white)",
          fontWeight: 700,
          marginBottom: "0.5rem",
        }}
      >
        Feedback
      </h2>
      <p
        style={{
          color: "#aaa",
          fontSize: "0.95rem",
          textAlign: "center",
          marginBottom: "2rem",
        }}
      >
        Help us improve Disbound. Share your thoughts, report bugs, or suggest features.
      </p>

      {/* Feedback submission form */}
      <form
        onSubmit={handleSubmit}
        style={{
          width: "100%",
          backgroundColor: "#1e1e2e",
          borderRadius: 16,
          padding: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        <textarea
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (submitted) setSubmitted(false);
          }}
          placeholder="What's on your mind?"
          rows={4}
          maxLength={2000}
          style={{
            width: "100%",
            backgroundColor: "#2a2a3e",
            border: "1px solid #3a3a4e",
            borderRadius: 12,
            color: "var(--main-white)",
            padding: "0.75rem 1rem",
            fontSize: "1rem",
            resize: "vertical",
            outline: "none",
          }}
          onFocus={(e) => (e.target.style.borderColor = "var(--main-purple)")}
          onBlur={(e) => (e.target.style.borderColor = "#3a3a4e")}
        />
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ marginTop: "0.75rem" }}
        >
          <span style={{ color: "#666", fontSize: "0.8rem" }}>
            {message.length}/2000
          </span>
          <button
            type="submit"
            disabled={submitting || !message.trim()}
            style={{
              backgroundColor: "var(--main-purple)",
              color: "#101010",
              border: "none",
              borderRadius: 24,
              padding: "0.5rem 1.5rem",
              fontWeight: 600,
              fontSize: "0.95rem",
              cursor: submitting || !message.trim() ? "not-allowed" : "pointer",
              opacity: submitting || !message.trim() ? 0.5 : 1,
            }}
          >
            {submitting ? "Sending..." : "Submit"}
          </button>
        </div>

        {submitted && (
          <div
            style={{
              marginTop: "0.75rem",
              color: "#4ade80",
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <ion-icon name="checkmark-circle"></ion-icon>
            Thanks for your feedback!
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: "0.75rem",
              color: "#f87171",
              fontSize: "0.9rem",
            }}
          >
            {error}
          </div>
        )}
      </form>

      {/* Admin: all feedback list */}
      {isAdmin && (
        <div style={{ width: "100%" }}>
          <h4
            style={{
              color: "var(--main-purple)",
              fontWeight: 600,
              marginBottom: "1rem",
            }}
          >
            All Feedback ({feedbackList.length})
          </h4>

          {loadingFeedback && (
            <div className="d-flex justify-content-center" style={{ padding: "2rem" }}>
              <div className="spinner-border" role="status" style={{ color: "var(--main-purple)" }}>
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {!loadingFeedback && feedbackList.length === 0 && (
            <p style={{ color: "#666", textAlign: "center" }}>
              No feedback yet.
            </p>
          )}

          {feedbackList.map((item) => {
            const avatarSrc =
              item.avatar && item.avatar !== "sampleAvatar.png"
                ? item.avatar
                : defaultAvatar;

            return (
              <div
                key={item.feedback_id}
                style={{
                  backgroundColor: "#1e1e2e",
                  borderRadius: 12,
                  padding: "1rem 1.25rem",
                  marginBottom: "0.75rem",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ gap: 10, marginBottom: "0.5rem" }}
                >
                  <img
                    src={avatarSrc}
                    alt={item.username}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  <span
                    style={{
                      color: "var(--main-purple)",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    @{item.username}
                  </span>
                  <span
                    style={{
                      color: "#666",
                      fontSize: "0.8rem",
                      marginLeft: "auto",
                    }}
                  >
                    {formatTime(item.created_at)}
                  </span>
                </div>
                <p
                  style={{
                    color: "var(--main-white)",
                    margin: 0,
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {item.message}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
